import React, { useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import CollectorTable from './components/collector-table';
import { IDataModal } from '@/interfaces/dataModal';
import {
  ICollector,
  ISearchCollectorReq,
  ISearchCollectorRes,
} from '@/interfaces/collector';
import { Button, Input, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { searchCollectors } from '@/adapters/collector';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import ImportCollectorModal from '@/components/import/collector/import-collector-modal';
import { IImportData } from '@/interfaces/import';
import CollectItemModal from '@/components/collector/collector-item-modal';
import { ECollectorType } from '@/enums/ECollector';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import { useAuth } from '@/contexts/AuthContext';

const _collectorModal: IDataModal<ICollector> = {
  open: false,
};

const { Search } = Input;

const _criteria: ISearchCollectorReq = {
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const _importModal: IDataModal<IImportData> = {
  open: false,
};

const Collector = () => {
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ISearchCollectorReq>({
    ..._criteria,
  });
  const [dataModal, setDataModal] =
    useState<IDataModal<ICollector>>(_collectorModal);

  const [importModal, setImportModal] =
    useState<IDataModal<IImportData>>(_importModal);

  const {
    data: collector,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<AxiosResponse<ISearchCollectorRes>, Error>({
    queryKey: ['search-collector', criteria],
    queryFn: async () => await searchCollectors(criteria),
  });

  const onActionCollector = () => {
    refetch();
    setDataModal({
      ..._collectorModal,
    });
    setImportModal({
      ..._importModal,
    });
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICollector> | SorterResult<ICollector>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof ICollector) || 'id';
      newCriteria.orderType = sorter.order || 'descend';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      pageStart: 1,
      pageSize: 10,
    });
  };

  return (
    <Card>
      <Title title="Collector">
        <div className="flex flex-row gap-2">
          <Button
            type="primary"
            className="max-w-[100px]"
            onClick={() => {
              setImportModal({
                open: true,
              });
            }}
          >
            <PlusOutlined /> เพิ่มใหม่
          </Button>
        </div>
      </Title>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row gap-3">
          <BusinessUnitSelector
            handleSelectData={function (id: number): void {
              setCriteria({ ...criteria, businessUnitId: id });
            }}
            disabled={!!userInfo?.bu}
          />
          <Search
            className="md:max-w-[400px] md:min-w-[400px]"
            placeholder="ค้นหา"
            allowClear
            size="large"
            enterButton="ค้นหา"
            onSearch={onSearch}
          />
        </div>
        <CollectorTable
          criteria={criteria}
          collector={collector?.data || { data: [], totalRecords: 0 }}
          loading={isLoading || isRefetching}
          onEditData={item =>
            setDataModal({ ..._collectorModal, id: item.id, item, open: true })
          }
          onDelete={onActionCollector}
          onTableChange={onTableChange}
        />
      </div>
      <ImportCollectorModal
        open={importModal?.open}
        handleOk={function (): void {
          onActionCollector();
        }}
        handleCancel={function (): void {
          onActionCollector();
        }}
      />
      <CollectItemModal
        items={dataModal?.item?.collectorItems || []}
        isBill={dataModal?.item?.collectorType === ECollectorType.BILLING}
        open={dataModal?.open}
        handleCancel={function (): void {
          setDataModal({
            ..._collectorModal,
          });
        }}
      />
    </Card>
  );
};

export default Collector;
